import { AlertCircle, Calendar, Check, CheckCircle2, Clock, ExternalLink, Github, Link2, Link2Off, Pencil, Phone, ShieldCheck, Tag, User, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAppContext } from "@/app/providers/AppContext";
import { isSlotConsumingWatch } from "@/domains/watches/lib/watchFormatting";
import type { ApiFn, Entitlements, IdentityProvider, Profile } from "@/shared/lib/types";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/ui/avatar";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/shared/ui/card";
import { Bar, T } from "@/shared/ui/DesignLib";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/ui/select";
import { Switch } from "@/shared/ui/switch";
import { TimezoneSelect } from "@/shared/ui/TimezoneSelect";
import AuditTrail from "./AuditTrail";

interface LatestInvoiceResponse {
  url?: string;
}

interface BillingPortalResponse {
  portal_url?: string;
}

interface AccountTabProps {
  profile: Profile | null;
  entitlements: Entitlements | null;
  api: ApiFn;
  onUpgrade: () => void;
}

function ProviderIcon({ provider }: { provider: string }) {
  if (provider === "github") return <Github size={15} className="text-foreground" />;
  return (
    <svg width="15" height="15" viewBox="0 0 48 48" fill="none">
      <path d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 7.9 2.9l5.7-5.7C34.5 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.9z" fill="#FFC107"/>
      <path d="M6.3 14.7l6.6 4.8C14.5 16 19 12 24 12c3.1 0 5.8 1.1 7.9 2.9l5.7-5.7C34.5 6.5 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 44c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.6C29.9 34.9 27.1 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.6 5.1C9.5 39.5 16.2 44 24 44z" fill="#4CAF50"/>
      <path d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.4l6.6 5.6C37.3 39 44 34 44 24c0-1.3-.1-2.7-.4-3.9z" fill="#1976D2"/>
    </svg>
  );
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const AccountTab = ({ profile, entitlements, api, onUpgrade }: AccountTabProps) => {
  const { watches } = useAppContext();
  const initials = (profile?.name || "U")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase();
  const profileAvatar = profile?.picture || profile?.avatar || profile?.photo || "";

  // ── Phase B: profile edit state ──────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(profile?.name ?? "");
  const [editEmail, setEditEmail] = useState(profile?.email ?? "");
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api("PATCH", "/api/user/profile", {
        name: editName,
        email: editEmail,
      });
      toast.success("Profile updated");
      setEditing(false);
      window.location.reload();
    } catch (e: unknown) {
      const msg = errorMessage(e, "Failed to update profile");
      toast.error(msg.includes("409") || msg.toLowerCase().includes("in use") ? "Email already in use by another account" : msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Timezone state (separate card, always editable) ─────────────
  const [tzMode, setTzMode] = useState<"et" | "local" | "custom">(profile?.timezone_mode ?? "local");
  const [tzCustom, setTzCustom] = useState(profile?.timezone ?? "");
  const [timeFormat, setTimeFormat] = useState<"12h" | "24h">(profile?.time_format ?? "24h");
  const [tzSaving, setTzSaving] = useState(false);

  const tzDirty =
    tzMode !== (profile?.timezone_mode ?? "local") ||
    tzCustom !== (profile?.timezone ?? "") ||
    timeFormat !== (profile?.time_format ?? "24h");

  const handleSaveTimezone = async () => {
    setTzSaving(true);
    try {
      await api("PATCH", "/api/user/profile", {
        timezone_mode: tzMode,
        timezone: tzMode === "custom" ? tzCustom : null,
        time_format: timeFormat,
      });
      toast.success("Timezone updated");
      window.location.reload();
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to update timezone"));
    } finally {
      setTzSaving(false);
    }
  };

  // ── Phase C: connected accounts state ────────────────────────────
  const [unlinkTarget, setUnlinkTarget] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const identityProviders: IdentityProvider[] = profile?.identity_providers ?? [];

  const handleUnlink = async (provider: string) => {
    setUnlinking(true);
    try {
      await api("DELETE", `/api/user/identity/providers/${provider}`);
      toast.success(`${provider} unlinked`);
      setUnlinkTarget(null);
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to unlink provider"));
    } finally {
      setUnlinking(false);
    }
  };

  // ── Phase D: phone verification state ────────────────────────────
  const [phoneStep, setPhoneStep] = useState<"input" | "otp" | "verified">(
    profile?.phone?.verified ? "verified" : "input"
  );
  const [phoneNumber, setPhoneNumber] = useState(profile?.phone?.number ?? "");
  const [otpCode, setOtpCode] = useState("");
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);

  const handleSendOtp = async () => {
    setPhoneSending(true);
    try {
      await api("POST", "/api/user/phone/send-otp", { phone: phoneNumber });
      setPhoneStep("otp");
      toast.success("Code sent!");
    } catch (e: unknown) {
      const msg = errorMessage(e, "");
      if (msg.includes("503") || msg.toLowerCase().includes("not configured")) {
        toast.error("Phone verification is not currently available");
      } else if (msg.includes("429") || msg.toLowerCase().includes("rate")) {
        toast.error("Too many attempts — please wait before resending");
      } else {
        toast.error(msg || "Failed to send code");
      }
    } finally {
      setPhoneSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    setPhoneVerifying(true);
    try {
      await api("POST", "/api/user/phone/verify-otp", { phone: phoneNumber, code: otpCode });
      setPhoneStep("verified");
      toast.success("Phone verified!");
    } catch (e: unknown) {
      const msg = errorMessage(e, "Verification failed");
      toast.error(msg.includes("422") || msg.toLowerCase().includes("invalid") ? "Invalid or expired code" : msg);
    } finally {
      setPhoneVerifying(false);
    }
  };

  // ── Phase E: promo code state ─────────────────────────────────────
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const redeemedCoupon = profile?.redeemed_coupon;
  const redeemedCouponAt = redeemedCoupon?.redeemed_at ? new Date(redeemedCoupon.redeemed_at) : null;

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      await api("POST", "/api/user/offers/redeem", { code: promoCode.trim() });
      toast.success("Promo code redeemed!");
      setPromoCode("");
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to redeem code"));
    } finally {
      setPromoLoading(false);
    }
  };

  // ── Phase F1: billing portal ──────────────────────────────────────
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const data = await api<BillingPortalResponse>("POST", "/api/billing/portal");
      if (data?.portal_url) window.location.href = data.portal_url;
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to open billing portal"));
    } finally {
      setPortalLoading(false);
    }
  };

  // ── Billing / invoice ─────────────────────────────────────────────
  const handleDownloadInvoice = async () => {
    try {
      const data = await api<LatestInvoiceResponse>("GET", "/api/billing/latest-invoice");
      if (data?.url) {
        window.open(data.url, "_blank");
        toast.success("Invoice download started");
      } else {
        toast.error("No invoices found");
      }
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to get invoice"));
    }
  };

  const usage: NonNullable<Entitlements["usage"]> = entitlements?.usage || profile?.usage || { active_watches: 0, total_alerts_fired: 0 };
  const quotas: Partial<NonNullable<Entitlements["quotas"]>> = entitlements?.quotas || profile?.quotas || {};
  const trialDays = profile?.trial_info?.trial_days_remaining ?? 0;
  const normalizedPlan = String(profile?.plan || "trial").toLowerCase();
  const isTrialPlan = normalizedPlan === "trial" || normalizedPlan === "trail";
  const maxActiveWatches = quotas.max_active_watches ?? 0;
  
  const activeWatches = watches ? watches.filter(w => isSlotConsumingWatch(w.status)).length : (usage.active_watches ?? 0);
  const usagePct = maxActiveWatches > 0 ? Math.round((activeWatches / maxActiveWatches) * 100) : 0;

  return (
    <div className="fade-up min-h-[88vh] space-y-6">
      {/* ── Hero / Profile Banner ── */}
      <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary via-primary/40 to-transparent" />
        <div className="relative flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-center gap-5">
            <Avatar className="h-16 w-16 shrink-0 rounded-2xl ring-2 ring-primary/25 ring-offset-2 ring-offset-card shadow-inner">
              <AvatarImage src={profileAvatar} alt={profile?.name || "Profile image"} />
              <AvatarFallback className="text-xl font-bold bg-primary/5 text-primary">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="mb-1.5 flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="type-label text-primary">Account Settings</span>
              </div>
              <h1 className="truncate text-2xl font-bold tracking-tight text-foreground">{profile?.name || "ANONYMOUS"}</h1>
              <p className="truncate text-sm text-muted-foreground">{profile?.email}</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {profile?.phone?.verified && (
              <Badge className="h-7 gap-1 border-gain/25 bg-gain/10 px-3 text-[10px] text-gain">
                <ShieldCheck size={12} />Verified
              </Badge>
            )}
            <Badge className="h-7 border-primary/25 bg-primary/10 px-3 text-[10px] text-primary">
              {profile?.plan?.toUpperCase() || "TRIAL"}
            </Badge>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 24 }} className="grid-cols-1 lg:grid-cols-[1.6fr_1fr] items-start">
        {/* ── Left column ── */}
        <div className="space-y-6">

          {/* Phase B + Profile Details */}
          <Card className="border-border/60 shadow-sm py-0 gap-0">
            <CardHeader className="pt-5 px-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <User size={16} />
                  </div>
                  <div>
                    <CardTitle className="text-base">Profile Details</CardTitle>
                    <CardDescription className="text-xs mt-0.5">Your personal identity information and account history</CardDescription>
                  </div>
                </div>
                {!editing && (
                  <Button variant="outline" size="sm" className="gap-1.5 border-primary/20 hover:border-primary/40 hover:bg-primary/5 hover:text-primary" onClick={() => { setEditName(profile?.name ?? ""); setEditEmail(profile?.email ?? ""); setEditing(true); }}>
                    <Pencil size={13} /> Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-5 pt-4 pb-6">
              {editing ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Full Name</label>
                    <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Your name" className="h-9" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Email Address</label>
                    <Input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="your@email.com" className="h-9" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={handleSaveProfile} disabled={saving} className="gap-1">
                      <Check size={13} />{saving ? "Saving…" : "Save"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="gap-1">
                      <X size={13} />Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Full Name</div>
                    <div className="text-sm font-semibold text-foreground">{profile?.name || "ANONYMOUS"}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Email Address</div>
                    <div className="text-sm font-semibold text-primary truncate">{profile?.email}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Account Created</div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Calendar size={14} className="text-primary/60" />
                      {profile?.created_at ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(profile.created_at)) : "—"}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Connected via</div>
                    <div className="flex gap-2">
                      {profile?.auth_providers?.map((p: string) => (
                        <Badge key={p} variant="outline" className="px-3 bg-primary/5 border-primary/20 text-primary text-[10px] h-6">{p.toUpperCase()}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase C — Connected Accounts */}
          <Card className="border-border/60 shadow-sm py-0 gap-0">
            <CardHeader className="pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Link2 size={16} />
                </div>
                <div>
                  <CardTitle className="text-base">Connected Accounts</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Manage which OAuth providers can sign you in</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pt-4 pb-5">
              <div className="space-y-1">
                {identityProviders.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No linked providers found. Link a provider below.</p>
                ) : (
                  identityProviders.map((ip) => (
                    <div key={ip.provider} className="flex items-center justify-between gap-4 rounded-lg py-3 px-3 -mx-3 border-b border-border/30 last:border-0 hover:bg-hover/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface border border-border/50">
                          <ProviderIcon provider={ip.provider} />
                        </div>
                        <div>
                          <div className="text-sm font-semibold capitalize">{ip.provider}</div>
                          {ip.email && <div className="text-xs text-muted-foreground">{ip.email}</div>}
                          {ip.linked_at && (
                            <div className="text-[10px] text-muted-foreground/60">
                              Linked {new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(ip.linked_at))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        className="gap-1 text-loss hover:text-loss hover:bg-loss/10"
                        disabled={identityProviders.length <= 1}
                        title={identityProviders.length <= 1 ? "Cannot remove your only sign-in method" : `Remove ${ip.provider}`}
                        onClick={() => setUnlinkTarget(ip.provider)}
                      >
                        <Link2Off size={13} />Unlink
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timezone & Time Format */}
          <Card className="border-border/60 shadow-sm py-0 gap-0">
            <CardHeader className="pt-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Clock size={16} />
                </div>
                <div>
                  <CardTitle className="text-base">Timezone & Time Format</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Choose how times are displayed across the dashboard</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pt-4 pb-5">
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">Timezone</label>
                  <Select value={tzMode} onValueChange={(v: "et" | "local" | "custom") => { setTzMode(v); if (v !== "custom") setTzCustom(""); }}>
                    <SelectTrigger className="h-9 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="et">Show ET time</SelectItem>
                      <SelectItem value="local">Show local time</SelectItem>
                      <SelectItem value="custom">Custom timezone</SelectItem>
                    </SelectContent>
                  </Select>
                  {tzMode === "custom" && (
                    <TimezoneSelect value={tzCustom} onChange={setTzCustom} />
                  )}
                </div>
                <div className="flex items-center justify-between gap-4 rounded-lg py-3 px-3 -mx-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface border border-border/50">
                      <Clock size={15} className="text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{timeFormat === "12h" ? "12-hour time (AM/PM)" : "24 hours Format"}</div>
                    </div>
                  </div>
                  <Switch
                    checked={timeFormat === "12h"}
                    onCheckedChange={(checked) => setTimeFormat(checked ? "12h" : "24h")}
                  />
                </div>
                {tzDirty && (
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={handleSaveTimezone} disabled={tzSaving} className="gap-1">
                    <Check size={13} />{tzSaving ? "Saving…" : "Save Timezone"}
                  </Button>
                </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Phase D — Phone Verification */}
          <Card className="border-border/60 shadow-sm py-0 gap-0">
            <CardHeader className="pt-5 px-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Phone size={16} />
                </div>
                <div>
                  <CardTitle className="text-base">Phone Verification</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Verify your phone to reduce your trial risk score</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-5 pt-4 pb-5">
              <div>
                {phoneStep === "verified" ? (
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-9 w-9 rounded-full bg-gain/10 border border-gain/25 shrink-0">
                      <CheckCircle2 size={18} className="text-gain" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">Phone verified</span>
                        <Badge className="border-gain/25 bg-gain/10 text-gain">Verified</Badge>
                      </div>
                      {profile?.phone?.number && (
                        <div className="text-xs text-muted-foreground mt-0.5">···· {profile.phone.number.slice(-4)}</div>
                      )}
                      {profile?.phone?.verified_at && (
                        <div className="text-[10px] text-muted-foreground/60">
                          {new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(profile.phone.verified_at))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : phoneStep === "otp" ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Enter the 6-digit code sent to {phoneNumber}.</p>
                    <div className="flex gap-2">
                      <Input value={otpCode} onChange={e => setOtpCode(e.target.value)} placeholder="123456" maxLength={6} className="h-9 w-36 font-mono tracking-widest" />
                      <Button size="sm" onClick={handleVerifyOtp} disabled={phoneVerifying || otpCode.length < 4}>
                        {phoneVerifying ? "Verifying…" : "Verify"}
                      </Button>
                    </div>
                    <button className="text-xs text-muted-foreground underline-offset-2 hover:underline" onClick={() => { setPhoneStep("input"); setOtpCode(""); }}>Back / resend</button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Enter your phone number in E.164 format (e.g. +12125551234).</p>
                    <div className="flex gap-2">
                      <Input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="+12125551234" className="h-9" />
                      <Button size="sm" onClick={handleSendOtp} disabled={phoneSending || !phoneNumber.startsWith("+")}>
                        {phoneSending ? "Sending…" : "Send Code"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Phase E — Promo Code */}
          <Card className="border-border/60 shadow-sm py-0 gap-0">
            <CardHeader className="pt-5 px-5">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Tag size={16} />
                </div>
                <div>
                  <CardTitle className="text-base">Promo Code</CardTitle>
                  <CardDescription className="text-xs mt-0.5">Have a promo code? Redeem it here</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4 px-5 pb-5">
              <div>
                {redeemedCoupon?.code ? (
                  <div className="flex items-center gap-3 py-1">
                    <div className="flex items-center justify-center h-9 w-9 rounded-full bg-gain/10 border border-gain/25 shrink-0">
                      <CheckCircle2 size={16} className="text-gain" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold font-mono tracking-wide">{redeemedCoupon.code}</span>
                        <Badge className="border-gain/25 bg-gain/10 text-gain">Applied</Badge>
                      </div>
                      {redeemedCouponAt && !Number.isNaN(redeemedCouponAt.getTime()) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Redeemed {new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(redeemedCouponAt)}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="PROMO CODE" className="h-9 font-mono tracking-wider uppercase" />
                    <Button size="sm" onClick={handleRedeemPromo} disabled={promoLoading || !promoCode.trim()}>
                      {promoLoading ? "Redeeming…" : "Redeem"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

        </div>{/* end left column */}

        {/* ── Right column ── */}
        <div className="space-y-6 lg:sticky lg:top-6">
          <Card className="border-primary/10">
            <CardHeader className="pt-0">
              <CardTitle className="text-lg">Subscription</CardTitle>
              <CardDescription>Manage your tier, billing status, and real-time resource usage.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex flex-col gap-1 p-4 rounded-lg bg-primary/5 border border-primary/10">
                  <div className="type-label text-primary">Current Tier</div>
                  <div className="text-2xl font-bold text-foreground font-mono tracking-tight">{profile?.plan?.toUpperCase() || "TRIAL"}</div>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-border/40">
                  <div className="text-sm text-muted-foreground font-medium flex items-center gap-2">
                    <div className={`h-1.5 w-1.5 rounded-full ${profile?.has_billing_account ? "bg-gain" : "bg-faint"}`} />
                    Billing Status
                  </div>
                  <Badge className={profile?.has_billing_account ? "border-gain/25 bg-gain/10 text-gain" : "border-border bg-surface text-muted-foreground"}>
                    {profile?.has_billing_account ? "Active" : "Not configured"}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between type-label text-muted-foreground">
                    Resource Usage
                    <span className="font-mono normal-case tracking-normal">{activeWatches} / {maxActiveWatches}</span>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center text-[10px] text-muted-foreground">
                      <span>Active Watches</span>
                      <span className="font-mono">{usagePct}%</span>
                    </div>
                    <Bar pct={usagePct} color={T.primary} />
                  </div>
                </div>

                {profile?.trial_info && (
                  <div className="p-3 rounded-lg bg-warning/5 border border-warning/20 flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Trial Days Left</span>
                    <Badge className="border-warning/25 bg-warning/10 text-warning">{trialDays} Days</Badge>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Button onClick={onUpgrade} className="w-full h-11">Manage Plan ↗</Button>
                {profile?.has_billing_account && (
                  <Button
                    variant="outline"
                    onClick={handleManageBilling}
                    disabled={portalLoading}
                    className="w-full h-11 flex items-center gap-2 border-primary/10 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all font-medium"
                  >
                    <ExternalLink size={15} className="text-primary/70" />
                    {portalLoading ? "Opening…" : "Manage Billing ↗"}
                  </Button>
                )}
                {!isTrialPlan && (
                  <Button
                    variant="outline"
                    onClick={handleDownloadInvoice}
                    className="w-full h-11 flex items-center gap-2 border-primary/10 hover:bg-primary/5 hover:border-primary/30 hover:text-primary transition-all font-medium"
                  >
                    <Calendar size={15} className="text-primary/70" />
                    Download Last Invoice
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-loss/20 bg-loss/5">
            <CardHeader>
              <div className="flex items-center gap-2 text-loss">
                <AlertCircle size={18} />
                <CardTitle className="text-sm font-bold uppercase tracking-widest text-loss">Danger Zone</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-0">
              <p className="text-xs text-muted-foreground">
                Permanently deactivate your account and all associated data. This action is irreversible and will immediately terminate all active watches and subscriptions.
              </p>
              <Button
                variant="destructive"
                className="w-full h-10 border border-loss/20 bg-loss text-white hover:bg-loss/90 transition-all"
                onClick={async () => {
                  if (confirm("Permanently delete your account? This cannot be undone.")) {
                    try {
                      await api("DELETE", "/api/user/account", { confirm: true });
                      window.location.href = "/login";
                    } catch {
                      // Local account deletion state is handled by the backend and redirect flow.
                    }
                  }
                }}
              >
                Deactivate Account
              </Button>
            </CardContent>
          </Card>
        </div>{/* end right column */}
      </div>

      {/* ── Login Activity / Audit Trail ── */}
      <AuditTrail api={api} />

      {/* Phase C — Unlink confirm dialog */}
      <Dialog open={!!unlinkTarget} onOpenChange={() => setUnlinkTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Unlink {unlinkTarget}</DialogTitle>
            <DialogDescription>
              Remove <strong className="capitalize">{unlinkTarget}</strong> as a sign-in method? You can re-link it later.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 pt-2">
            <Button
              variant="destructive"
              className="flex-1"
              disabled={unlinking}
              onClick={() => unlinkTarget && handleUnlink(unlinkTarget)}
            >
              {unlinking ? "Unlinking…" : "Remove"}
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => setUnlinkTarget(null)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountTab;
